import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAcademicSubjects,
  fetchSubjectTree,
  fetchTopic,
  fetchSubjectProgress,
  setTopicProgress,
  searchAcademic,
  fetchMySubject,
  setMySubject,
  sanitizeSubjectSlug,
} from '../api/academic';
import type { ProgressStatus } from '../types';

export { sanitizeSubjectSlug };

export function useAcademicSubjects(paper?: 'I' | 'II') {
  return useQuery({
    queryKey: ['academic', 'subjects', paper ?? 'all'],
    queryFn: () => fetchAcademicSubjects(paper),
  });
}

export function useSubjectTree(slug: string) {
  return useQuery({
    queryKey: ['academic', 'tree', slug],
    queryFn: () => fetchSubjectTree(slug),
    enabled: Boolean(slug),
  });
}

export function useTopic(id: number | string) {
  return useQuery({
    queryKey: ['academic', 'topic', String(id)],
    queryFn: () => fetchTopic(id),
    enabled: Boolean(id),
  });
}

// Progress is per-user; only fetch when the caller says the visitor is authed.
export function useSubjectProgress(slug: string, enabled: boolean) {
  return useQuery({
    queryKey: ['academic', 'progress', slug],
    queryFn: () => fetchSubjectProgress(slug),
    enabled: enabled && Boolean(slug),
  });
}

// Marking a topic invalidates the subject progress rollups so the UI updates.
export function useSetTopicProgress(subjectSlug?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ topicId, status }: { topicId: number; status: ProgressStatus }) =>
      setTopicProgress(topicId, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['academic', 'progress'] });
      if (subjectSlug) {
        qc.invalidateQueries({ queryKey: ['academic', 'progress', subjectSlug] });
      }
    },
  });
}

export function useAcademicSearch(query: string) {
  return useQuery({
    queryKey: ['academic', 'search', query],
    queryFn: () => searchAcademic(query),
    enabled: query.trim().length >= 2,
  });
}

// The learner's selected Paper II subject; only fetched when signed in.
export function useMySubject(enabled: boolean) {
  return useQuery({
    queryKey: ['academic', 'mySubject'],
    queryFn: fetchMySubject,
    enabled,
  });
}

export function useSelectSubject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, confirmReset, selectedSlugs }: { slug: string; confirmReset?: boolean; selectedSlugs?: string[] }) =>
      setMySubject(slug, confirmReset, selectedSlugs),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['academic', 'mySubject'] });
      qc.invalidateQueries({ queryKey: ['academic', 'progress'] });
      qc.invalidateQueries({ queryKey: ['student', 'live-tests'] });
    },
  });
}
